import { API_BASE, AUTH_TOKEN } from '../config';

// Listening rooms — an intimate, invite-only session in the room you are in.
//
// Khari, 2026-08-04: "The room gets a code, and the invite goes out. when the
// participants join (as their avatars), their models are placed, sitting on the
// couch, chair or some other room appropriate position."
//
// CAPACITY IS COUNTED HERE AND OWNED THERE. Only the client can see the
// furniture — seats are an affordance on the room's GLB — so `open()` reports
// how many it found and the server clamps and then owns the number. Seat
// ASSIGNMENT is always the server's: it hands back an INDEX, and
// `services/seats.js:listSeats()` turns that index into the chair, in an order
// every client agrees on.
const hdr = () => ({
  'Content-Type': 'application/json',
  ...(AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {}),
});

async function call(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${API_BASE}/api/listening${path}`, {
    method, headers: hdr(), ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `listening room unavailable (${res.status})`);
  return d;
}

/** Open a room where you are standing. `seatCount` comes from listSeats(). */
export async function openRoom({ zone, seatCount, avatarId }) {
  const d = await call('/open', { method: 'POST', body: { zone, seatCount, avatarId } });
  return d.room;
}

/** Ask an artist by handle. Never throws away the code — sharing still works. */
export async function invite(roomId, handle) {
  return call(`/${roomId}/invite`, { method: 'POST', body: { handle } });
}

/** Take a seat. Re-entrant: a refresh returns the seat you already had. */
export async function join(code, avatarId) {
  return call('/join', { method: 'POST', body: { code, avatarId } });
}

export async function leave(roomId) {
  return call(`/${roomId}/leave`, { method: 'POST' });
}

/**
 * What the room is hearing. Host only.
 *
 * No position is sent — the server stamps `startedAt` and every client seeks to
 * `(now - startedAt)`. One clock, so nobody drifts, and somebody arriving
 * halfway through lands halfway through.
 */
export async function setNowPlaying(roomId, track) {
  return call(`/${roomId}/now-playing`, { method: 'POST', body: track });
}

/**
 * Offer the empty chairs to a resident.
 *
 * Khari: "perhaps a 'resident' can join you if the user doesn't respond. the
 * avatar (if free) can join on its own."
 *
 * `candidates` are avatar ids WE can actually draw — roster.json lives here, and
 * the server deliberately keeps no copy of it. The server decides which of them
 * is free (not working a role shift), so the two halves each answer the question
 * they are able to.
 */
export async function fillSeat(roomId, candidates, { afterMs = 45000, force = false } = {}) {
  return call(`/${roomId}/fill`, { method: 'POST', body: { candidates, afterMs, force } });
}

/** Poll a room by code — the fallback when the socket is not carrying updates. */
export async function roomByCode(code) {
  const d = await call(`/${encodeURIComponent(code)}`);
  return d.room;
}

/** Anything open in this room right now — so a walk-in can see it and sit down. */
export async function roomsInZone(zone) {
  try {
    const d = await call(`/zone/${encodeURIComponent(zone)}`);
    return d.rooms || [];
  } catch {
    return [];   // a quiet room is a fine failure
  }
}

/**
 * Where playback should be, in seconds, for a room's current track.
 *
 * The whole of the sync, and it is arithmetic rather than a protocol: no
 * heartbeat, nothing to miss, and correct the first frame after you arrive.
 */
export function playheadOf(nowPlaying, now = Date.now()) {
  if (!nowPlaying?.startedAt) return 0;
  const t = (now - nowPlaying.startedAt) / 1000;
  if (!Number.isFinite(t) || t < 0) return 0;
  // Past the end is a legitimate state — the host has not queued the next one
  // yet — and reporting the real number lets the caller decide to stop rather
  // than seek into nowhere.
  return t;
}
